import type { Account } from '../types/account'
import type { IpAddressAllocation } from '../types/cluster'
import { formatIpAllocationsForTable } from './accountRequestNetworkSummary'
import { migrateLegacyIpLabel } from './ipAllocationValidation'

export type DiscoveryTagSelectOption = {
	label: string
	value: string
	description?: string
	purpose: string
}

export function ipAllocationLabelToDiscoveryPurpose(label: string): string {
	const normalized = migrateLegacyIpLabel(label).trim().toLowerCase().replace(/\s+/g, '-')
	if (normalized === 'workernode') return 'WORKERNODE'
	if (normalized === 'shared-workernode') return 'SHARED-WORKERNODE'
	if (normalized === 'pods') return 'PODS'
	return label.trim().toUpperCase().replace(/\s+/g, '-')
}

function ipAllocationsFromDetails(
	details: Record<string, unknown> | undefined | null
): IpAddressAllocation[] {
	return formatIpAllocationsForTable(details?.ip_address_allocations)
}

/** Resolve approval network bag whether stored flat or under `approval.details`. */
function networkBagFromApproval(
	approval: Record<string, unknown> | undefined | null
): Record<string, unknown> | undefined {
	if (!approval || typeof approval !== 'object') return undefined
	const nested = approval.details
	if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
		const fromNested = nested as Record<string, unknown>
		if (ipAllocationsFromDetails(fromNested).length > 0) return fromNested
	}
	if (ipAllocationsFromDetails(approval).length > 0) return approval
	if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
		return nested as Record<string, unknown>
	}
	return approval
}

/** IP allocations from approved account network details by project env:
 * - st → STG network (account details / stgRequest)
 * - dv / pd / sandbox (and other) → that linked account's network approval
 */
export function getAccountIpAddressAllocations(
	account: Account | null | undefined,
	projectEnvironment: string
): IpAddressAllocation[] {
	if (!account) return []
	const env = String(projectEnvironment ?? '').trim().toLowerCase()

	if (env === 'st' || env === 'stg' || env === 'staging') {
		const details = (account.details ?? {}) as Record<string, unknown>
		const stgDetails = (account.stgRequest?.details ?? {}) as Record<string, unknown>
		const stgStatus = String(account.stgRequest?.status ?? '')
			.trim()
			.toUpperCase()

		// While STG follow-up is pending, details still holds the original (e.g. DV) network.
		if (stgStatus === 'PENDING_APPROVAL') {
			const fromStg = ipAllocationsFromDetails(stgDetails)
			if (fromStg.length > 0) return fromStg
		}

		const fromDetails = ipAllocationsFromDetails(details)
		if (fromDetails.length > 0) return fromDetails
		return ipAllocationsFromDetails(stgDetails)
	}

	const approvalBag = networkBagFromApproval(
		account.approval as Record<string, unknown> | undefined
	)
	const fromApproval = ipAllocationsFromDetails(approvalBag)
	if (fromApproval.length > 0) return fromApproval

	return ipAllocationsFromDetails((account.details ?? {}) as Record<string, unknown>)
}

function rowHasCidr(row: IpAddressAllocation): boolean {
	return Boolean(
		row.cidrAzA?.trim() || row.cidrAzB?.trim() || row.cidrAzC?.trim()
	)
}

export function buildDiscoveryTagOptionsFromIpAllocations(
	allocations: IpAddressAllocation[]
): DiscoveryTagSelectOption[] {
	return allocations
		.filter((row) => row.label?.trim() && rowHasCidr(row))
		.map((row, index) => {
			const label = migrateLegacyIpLabel(row.label).trim()
			const cidrs = [row.cidrAzA, row.cidrAzB, row.cidrAzC]
				.map((c) => c?.trim())
				.filter(Boolean)
			return {
				label,
				value: `ip-alloc-${index}`,
				description: cidrs.length > 0 ? cidrs.join(' · ') : undefined,
				purpose: ipAllocationLabelToDiscoveryPurpose(label)
			}
		})
}

export type SubnetAliasFromIpAllocation = {
	alias: string
	count: string
	identifier: string
	value: string
}

/**
 * Prefill project-approval subnet alias rows from account IP allocations.
 * Value/purpose come from allocation labels (e.g. WORKERNODE); identifier is tag:Purpose.
 */
export function buildSubnetAliasRowsFromIpAllocations(
	allocations: IpAddressAllocation[]
): SubnetAliasFromIpAllocation[] {
	return allocations
		.filter((row) => row.label?.trim() && rowHasCidr(row))
		.map((row) => {
			const purpose = ipAllocationLabelToDiscoveryPurpose(row.label)
			const azCount = [row.cidrAzA, row.cidrAzB, row.cidrAzC]
				.map((c) => c?.trim())
				.filter(Boolean).length
			return {
				alias: purpose.toLowerCase(),
				count: azCount > 0 ? String(azCount) : '',
				identifier: 'tag:Purpose',
				value: purpose
			}
		})
}

/** True when the subnet-alias table is only empty placeholders (safe to overwrite). */
export function subnetAliasRowsArePlaceholders(
	rows: Array<{
		alias?: string
		count?: string
		identifier?: string
		value?: string
	}>
): boolean {
	if (!rows?.length) return true
	return rows.every(
		(row) =>
			!String(row.alias ?? '').trim() &&
			!String(row.count ?? '').trim() &&
			!String(row.identifier ?? '').trim() &&
			!String(row.value ?? '').trim()
	)
}
